import React, { useState, useEffect, useCallback } from 'react';
import {useSearchParams} from 'react-router-dom';import {useAdminTheme} from '@/contexts/AdminThemeContext';import {Search, ChevronLeft, ChevronRight, User, Loader2, Gift, Users as UsersIcon, Filter, X, CheckSquare, Square, Download, MapPin, Ban, KeyRound, Trash2, Shield, FlaskConical} from 'lucide-react';import {Button} from '@/components/ui/button';import {toast} from 'sonner';import AdminLayout from './AdminLayout';
import UserJourneyDrawer from './UserJourneyDrawer';
import axios from 'axios';
import {API_URL as API} from '@/utils/apiConfig';

const tierBadge = {
  free: 'bg-slate-500/15 text-slate-400',
  paid: 'bg-indigo-500/15 text-indigo-400',
  premium: 'bg-amber-500/15 text-amber-400',
  share: 'bg-emerald-500/15 text-emerald-400',
};

export default function AdminUsersPage() {
  const { isDark, colors } = useAdminTheme();
  const [searchParams] = useSearchParams();
  const [users, setUsers] = useState([]);
  const [total, setTotal] = useState(0);
  const [pages, setPages] = useState(1);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [tierFilter, setTierFilter] = useState(searchParams.get('tier') || 'all');
  const [subStatusFilter, setSubStatusFilter] = useState(searchParams.get('sub_status') || 'all');
  const [countryFilter, setCountryFilter] = useState(searchParams.get('country') || '');
  const [loading, setLoading] = useState(true);
  const [selectedUser, setSelectedUser] = useState(null);
  const [provisionModal, setProvisionModal] = useState(null);
  const [bulkModal, setBulkModal] = useState(false);
  const [selectedUserIds, setSelectedUserIds] = useState([]);
  const [showTest, setShowTest] = useState(false);
  const [joinedFilter, setJoinedFilter] = useState('');
  const [countriesList, setCountriesList] = useState([]);

  // Fetch countries for dropdown
  useEffect(() => {
    axios.get(`${API}/admin/users/countries`, { withCredentials: true })
      .then(res => setCountriesList(res.data))
      .catch(() => {});
  }, []);

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    try {
      const params = { search, tier: tierFilter, page, limit: 15, show_test: showTest ? 'true' : 'false' };
      if (countryFilter) params.country = countryFilter;
      if (joinedFilter) params.joined = joinedFilter;
      if (subStatusFilter && subStatusFilter !== 'all') params.sub_status = subStatusFilter;
      const res = await axios.get(`${API}/admin/users`, {
        params,
        withCredentials: true,
      });
      setUsers(res.data.users);
      setTotal(res.data.total);
      setPages(res.data.pages);
    } catch (err) {
      toast.error('Failed to load users');
    } finally {
      setLoading(false);
    }
  }, [search, tierFilter, subStatusFilter, countryFilter, joinedFilter, page, showTest]);

  useEffect(() => { fetchUsers(); }, [fetchUsers]);

  const handleChangeTier = async (userId, newTier) => {
    try {
      await axios.put(`${API}/admin/users/${userId}/tier`, { tier: newTier }, { withCredentials: true });
      toast.success(`Tier updated to ${newTier}`);
      fetchUsers();
      if (selectedUser?.user_id === userId) {
        setSelectedUser((prev) => ({ ...prev, subscription_tier: newTier }));
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to update tier');
    }
  };

  const handleBlock = async (userId) => {
    try {
      const res = await axios.post(`${API}/admin/users/${userId}/block`, {}, { withCredentials: true });
      toast.success(`User ${res.data.is_blocked ? 'blocked' : 'unblocked'}`);
      fetchUsers();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Action failed');
    }
  };

  const handleSendPasswordReset = async (userId, email) => {
    try {
      await axios.post(`${API}/admin/users/${userId}/send-password-reset`, {}, { withCredentials: true });
      toast.success(`Reset email sent to ${email}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to send reset');
    }
  };

  const [deleteModal, setDeleteModal] = useState(null);

  const handleToggleTest = async (userId) => {
    try {
      const res = await axios.post(`${API}/admin/users/${userId}/toggle-test`, {}, { withCredentials: true });
      toast.success(`User ${res.data.is_test_account ? 'marked as test' : 'marked as real'}`);
      fetchUsers();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Action failed');
    }
  };

  const handleBulkFlagTest = async () => {
    try {
      const res = await axios.post(`${API}/admin/users/bulk-flag-test`, {}, { withCredentials: true });
      toast.success(`Flagged ${res.data.flagged_count} legacy test account(s)`);
      fetchUsers();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Bulk flag failed');
    }
  };

  const inputStyle = { background: colors.input, border: `1px solid ${colors.inputBorder}`, color: colors.text };

  return (
    <AdminLayout>
      <div className="space-y-5" data-testid="admin-users-page">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <h1 className="text-xl font-bold" style={{ color: colors.text }}>Users</h1>
          <div className="flex items-center gap-3">
            <button
              onClick={() => window.open(`${API}/admin/export/users`, '_blank')}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition"
              style={{ background: colors.card, border: `1px solid ${colors.cardBorder}`, color: colors.textSecondary }}
              data-testid="export-users-btn"
            >
              <Download className="h-3.5 w-3.5" /> Export CSV
            </button>
            <span className="text-sm" style={{ color: colors.textSecondary }}>{total} total</span>
          </div>
        </div>

        {/* Real/Test Toggle */}
        <div className="flex items-center gap-2" data-testid="test-toggle-container">
          <button
            onClick={() => { setShowTest(false); setPage(1); }}
            className={`px-3 py-1.5 rounded-lg text-xs font-medium transition ${!showTest ? 'ring-1' : ''}`}
            style={{
              background: !showTest ? (isDark ? 'rgba(99,102,241,0.15)' : 'rgba(99,102,241,0.1)') : colors.card,
              color: !showTest ? '#818cf8' : colors.textMuted,
              border: `1px solid ${!showTest ? 'rgba(99,102,241,0.3)' : colors.cardBorder}`,
            }}
            data-testid="show-real-users-btn"
          >
            <UsersIcon className="h-3.5 w-3.5 inline mr-1.5" />Real Users
          </button>
          <button
            onClick={() => { setShowTest(true); setPage(1); }}
            className={`px-3 py-1.5 rounded-lg text-xs font-medium transition ${showTest ? 'ring-1' : ''}`}
            style={{
              background: showTest ? (isDark ? 'rgba(234,179,8,0.15)' : 'rgba(234,179,8,0.1)') : colors.card,
              color: showTest ? '#eab308' : colors.textMuted,
              border: `1px solid ${showTest ? 'rgba(234,179,8,0.3)' : colors.cardBorder}`,
            }}
            data-testid="show-test-users-btn"
          >
            <FlaskConical className="h-3.5 w-3.5 inline mr-1.5" />Test Users
          </button>
          {showTest && (
            <button
              onClick={handleBulkFlagTest}
              className="px-3 py-1.5 rounded-lg text-xs font-medium transition"
              style={{
                background: isDark ? 'rgba(234,179,8,0.08)' : 'rgba(234,179,8,0.05)',
                color: colors.textMuted,
                border: `1px solid ${colors.cardBorder}`,
              }}
              data-testid="bulk-flag-test-btn"
              title="Auto-flag all users with test domain emails (@test.com, @example.com, etc.)"
            >
              <FlaskConical className="h-3.5 w-3.5 inline mr-1.5" />Auto-flag Legacy
            </button>
          )}
        </div>

        {/* Filters */}
        <div className="flex flex-wrap gap-3">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4" style={{ color: colors.textMuted }} />
            <input
              value={search}
              onChange={(e) => { setSearch(e.target.value); setPage(1); }}
              placeholder="Search by name or email..."
              className="w-full rounded-xl pl-10 pr-4 py-2.5 text-sm placeholder-slate-500 focus:outline-none transition"
              style={inputStyle}
              data-testid="admin-user-search"
            />
          </div>
          <select
            value={tierFilter}
            onChange={(e) => { setTierFilter(e.target.value); setPage(1); }}
            className="rounded-xl px-4 py-2.5 text-sm focus:outline-none"
            style={inputStyle}
            data-testid="admin-tier-filter"
          >
            <option value="all">All Tiers</option>
            <option value="subscribers">All Subscribers</option>
            <option value="free">Free</option>
            <option value="paid">Paid</option>
            <option value="premium">Premium</option>
            <option value="share">Share</option>
          </select>
          <select
            value={subStatusFilter}
            onChange={(e) => { setSubStatusFilter(e.target.value); setPage(1); }}
            className="rounded-xl px-4 py-2.5 text-sm focus:outline-none"
            style={inputStyle}
            data-testid="admin-sub-status-filter"
            title="Filter paying customers vs trial users"
          >
            <option value="all">All Statuses</option>
            <option value="paying">💳 Paying</option>
            <option value="trialing">🎁 On Trial</option>
            <option value="none">No Subscription</option>
          </select>
          {countryFilter && (
            <div className="flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-medium bg-emerald-500/15 text-emerald-400 border border-emerald-500/20" data-testid="country-filter-badge">
              <MapPin className="h-3.5 w-3.5" />
              <span>{countryFilter}</span>
              <button onClick={() => { setCountryFilter(''); setPage(1); }} className="ml-1 hover:text-white transition" data-testid="clear-country-filter">
                <X className="h-3.5 w-3.5" />
              </button>
            </div>
          )}

          {/* Country filter dropdown */}
          <select
            value={countryFilter}
            onChange={e => { setCountryFilter(e.target.value); setPage(1); }}
            className="rounded-xl text-xs px-3 py-2 border focus:outline-none focus:ring-1 focus:ring-indigo-500/50 appearance-none cursor-pointer"
            style={{ background: colors.card, borderColor: colors.cardBorder, color: colors.text }}
            data-testid="country-filter-select"
          >
            <option value="">All Countries</option>
            {countriesList.map(c => (
              <option key={c.country} value={c.country}>{c.country} ({c.count})</option>
            ))}
          </select>

          {/* Joined date filter */}
          <select
            value={joinedFilter}
            onChange={e => { setJoinedFilter(e.target.value); setPage(1); }}
            className="rounded-xl text-xs px-3 py-2 border focus:outline-none focus:ring-1 focus:ring-indigo-500/50 appearance-none cursor-pointer"
            style={{ background: colors.card, borderColor: colors.cardBorder, color: colors.text }}
            data-testid="joined-filter-select"
          >
            <option value="">All Time</option>
            <option value="7d">Last 7 days</option>
            <option value="30d">Last 30 days</option>
            <option value="90d">Last 90 days</option>
            <option value="180d">Last 6 months</option>
            <option value="365d">Last year</option>
          </select>
        </div>

        {/* Bulk Action Bar */}
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div className="flex items-center gap-2">
            {selectedUserIds.length > 0 && (
              <>
                <span className="text-xs" style={{ color: colors.textSecondary }}>{selectedUserIds.length} selected</span>
                <button onClick={() => setSelectedUserIds([])} className="text-xs transition" style={{ color: colors.textMuted }}>Clear</button>
              </>
            )}
          </div>
          <Button
            onClick={() => setBulkModal(true)}
            className="rounded-xl bg-emerald-600 hover:bg-emerald-500 text-white gap-2 text-xs h-8 px-3"
            data-testid="bulk-provision-btn"
          >
            <Gift className="h-3.5 w-3.5" /> Bulk Provision Trials
          </Button>
        </div>

        {/* Table */}
        <div className="rounded-2xl overflow-hidden" style={{ background: colors.card, border: `1px solid ${colors.cardBorder}` }}>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-xs uppercase tracking-wider" style={{ background: isDark ? 'rgba(30,41,59,0.4)' : 'rgba(241,245,249,0.8)', color: colors.textSecondary }}>
                  <th className="text-left px-3 py-3 w-10">
                    <button
                      onClick={() => {
                        const allIds = users.map(u => u.user_id);
                        const allSelected = allIds.every(id => selectedUserIds.includes(id));
                        setSelectedUserIds(allSelected ? selectedUserIds.filter(id => !allIds.includes(id)) : [...new Set([...selectedUserIds, ...allIds])]);
                      }}
                      style={{ color: colors.textMuted }}
                      data-testid="select-all-checkbox"
                    >
                      {users.length > 0 && users.every(u => selectedUserIds.includes(u.user_id))
                        ? <CheckSquare className="h-4 w-4 text-indigo-400" />
                        : <Square className="h-4 w-4" />}
                    </button>
                  </th>
                  <th className="text-left px-4 py-3 font-medium">User</th>
                  <th className="text-left px-4 py-3 font-medium">Tier</th>
                  <th className="text-left px-4 py-3 font-medium hidden sm:table-cell">Messages</th>
                  <th className="text-left px-4 py-3 font-medium hidden md:table-cell">Last Active</th>
                  <th className="text-right px-4 py-3 font-medium">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y" style={{ borderColor: colors.cardBorder }}>
                {loading ? (
                  <tr><td colSpan={6} className="text-center py-12"><Loader2 className="h-5 w-5 animate-spin mx-auto" style={{ color: colors.textMuted }} /></td></tr>
                ) : users.length === 0 ? (
                  <tr><td colSpan={6} className="text-center py-12" style={{ color: colors.textMuted }}>No users found</td></tr>
                ) : (
                  users.map((u) => (
                    <tr key={u.user_id} className="transition" style={{ ':hover': { background: isDark ? 'rgba(30,41,59,0.3)' : 'rgba(241,245,249,0.5)' } }} data-testid={`user-row-${u.user_id}`}>
                      <td className="px-3 py-3">
                        <button
                          onClick={() => setSelectedUserIds(prev => prev.includes(u.user_id) ? prev.filter(id => id !== u.user_id) : [...prev, u.user_id])}
                          style={{ color: colors.textMuted }}
                          data-testid={`select-user-${u.user_id}`}
                        >
                          {selectedUserIds.includes(u.user_id) ? <CheckSquare className="h-4 w-4 text-indigo-400" /> : <Square className="h-4 w-4" />}
                        </button>
                      </td>
                      <td className="px-4 py-3 cursor-pointer" onClick={() => setSelectedUser(u)} data-testid={`user-name-cell-${u.user_id}`}>
                        <div className="flex items-center gap-1.5">
                          {u.country_code && <span className="text-sm leading-none" title={u.country}>{u.country_code.toUpperCase().replace(/./g, c => String.fromCodePoint(127397 + c.charCodeAt()))}</span>}
                          <p className="font-medium truncate max-w-[200px]" style={{ color: colors.text }}>{u.name || 'Unnamed'}</p>
                          {u.auth_method === 'google' && <span className="px-1.5 py-0.5 rounded text-[9px] font-bold uppercase bg-yellow-500/12 text-yellow-400">G</span>}
                          {u.is_blocked && <span className="px-1.5 py-0.5 rounded text-[9px] font-bold uppercase bg-red-500/20 text-red-400">Blocked</span>}
                          {u.is_test_account && <span className="px-1.5 py-0.5 rounded text-[9px] font-bold uppercase bg-yellow-500/20 text-yellow-400" data-testid={`test-badge-${u.user_id}`}>Test</span>}
                        </div>
                        <p className="text-xs truncate max-w-[200px]" style={{ color: colors.textMuted }}>{u.email}</p>
                        {u.username && <p className="text-[11px] text-indigo-400 truncate max-w-[200px]">@{u.username}</p>}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-1 flex-wrap">
                          <span className={`px-2 py-0.5 rounded-full text-[11px] font-semibold uppercase ${tierBadge[u.subscription_tier] || tierBadge.free}`}>
                            {u.subscription_tier || 'free'}
                          </span>
                          {u.is_trial && (
                            <span
                              className="px-1.5 py-0.5 rounded text-[9px] font-bold uppercase bg-amber-500/15 text-amber-400 border border-amber-500/20"
                              title={u.trial_ends_at ? `Trial ends ${new Date(u.trial_ends_at).toLocaleDateString()}${u.trial_days_remaining != null ? ` (${u.trial_days_remaining}d left)` : ''}` : 'Trial user'}
                              data-testid={`trial-badge-${u.user_id}`}
                            >
                              Trial
                            </span>
                          )}
                          {!u.is_trial && ['paid', 'premium', 'share'].includes(u.subscription_tier) && (
                            <span
                              className="px-1.5 py-0.5 rounded text-[9px] font-bold uppercase bg-emerald-500/15 text-emerald-400 border border-emerald-500/20"
                              title={u.mrr ? `${u.mrr_currency || 'USD'} ${u.mrr}/mo${u.subscription_source ? ` · ${u.subscription_source}` : ''}` : (u.paying_since ? `Paying since ${new Date(u.paying_since).toLocaleDateString()}` : 'Paying customer (subscription source unknown)')}
                              data-testid={`paying-badge-${u.user_id}`}
                            >
                              Paying
                            </span>
                          )}
                          {u.share_role && (
                            <span className={`px-1.5 py-0.5 rounded text-[9px] font-bold uppercase ${u.share_role === 'owner' ? 'bg-emerald-500/25 text-emerald-300' : 'bg-sky-500/20 text-sky-400'}`}>
                              {u.share_role}
                            </span>
                          )}
                        </div>
                        {/* Subscription lifecycle subtitle — makes paying-since flow visible at a glance */}
                        {(u.paying_since || u.trial_ends_at) && (
                          <p className="text-[10px] mt-1" style={{ color: colors.textMuted }} data-testid={`sub-timeline-${u.user_id}`}>
                            {u.is_trial && u.trial_ends_at ? (
                              <>Trial ends {new Date(u.trial_ends_at).toLocaleDateString()}{u.trial_days_remaining != null ? ` · ${u.trial_days_remaining}d left` : ''}</>
                            ) : u.paying_since ? (
                              <>Paying since {new Date(u.paying_since).toLocaleDateString()}{u.mrr ? ` · ${u.mrr_currency || 'USD'} ${u.mrr}/mo` : ''}</>
                            ) : null}
                            {u.subscription_source && <span className="ml-1 opacity-70">· {u.subscription_source}</span>}
                          </p>
                        )}
                      </td>
                      <td className="px-4 py-3 hidden sm:table-cell" style={{ color: colors.textSecondary }}>{u.message_count}</td>
                      <td className="px-4 py-3 text-xs hidden md:table-cell" style={{ color: colors.textMuted }}>
                        {u.last_active ? new Date(u.last_active).toLocaleDateString() : '—'}
                      </td>
                      <td className="px-4 py-3 text-right">
                        <div className="flex items-center justify-end gap-1">
                          <select
                            value={u.subscription_tier || 'free'}
                            onChange={(e) => handleChangeTier(u.user_id, e.target.value)}
                            className="rounded-lg px-2 py-1 text-xs focus:outline-none"
                            style={{ background: colors.input, border: `1px solid ${colors.inputBorder}`, color: colors.text }}
                            data-testid={`tier-select-${u.user_id}`}
                          >
                            <option value="free">Free</option>
                            <option value="paid">Paid</option>
                            <option value="premium">Premium</option>
                            <option value="share">Share</option>
                          </select>
                          <Button size="sm" variant="ghost" className="h-7 px-2 text-xs text-indigo-400 hover:text-indigo-300 hover:bg-indigo-500/10"
                            onClick={() => setProvisionModal(u)} data-testid={`provision-btn-${u.user_id}`} title="Trial">
                            <Gift className="h-3 w-3" />
                          </Button>
                          <Button size="sm" variant="ghost" className="h-7 px-2 text-xs text-blue-400 hover:text-blue-300 hover:bg-blue-500/10"
                            onClick={() => handleSendPasswordReset(u.user_id, u.email)} data-testid={`reset-pwd-btn-${u.user_id}`} title="Send password reset">
                            <KeyRound className="h-3 w-3" />
                          </Button>
                          <Button size="sm" variant="ghost" className={`h-7 px-2 text-xs ${u.is_blocked ? 'text-emerald-400 hover:text-emerald-300 hover:bg-emerald-500/10' : 'text-amber-400 hover:text-amber-300 hover:bg-amber-500/10'}`}
                            onClick={() => handleBlock(u.user_id)} data-testid={`block-btn-${u.user_id}`} title={u.is_blocked ? 'Unblock' : 'Block'}>
                            <Ban className="h-3 w-3" />
                          </Button>
                          <Button size="sm" variant="ghost" className="h-7 px-2 text-xs text-red-400 hover:text-red-300 hover:bg-red-500/10"
                            onClick={() => setDeleteModal(u)} data-testid={`delete-btn-${u.user_id}`} title="Delete">
                            <Trash2 className="h-3 w-3" />
                          </Button>
                          <Button size="sm" variant="ghost" className={`h-7 px-2 text-xs ${u.is_test_account ? 'text-yellow-400 hover:text-yellow-300 hover:bg-yellow-500/10' : 'text-slate-400 hover:text-slate-300 hover:bg-slate-500/10'}`}
                            onClick={() => handleToggleTest(u.user_id)} data-testid={`toggle-test-btn-${u.user_id}`} title={u.is_test_account ? 'Mark as real' : 'Mark as test'}>
                            <FlaskConical className="h-3 w-3" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Pagination */}
        {pages > 1 && (
          <div className="flex items-center justify-center gap-3">
            <Button
              variant="ghost"
              size="sm"
              disabled={page <= 1}
              onClick={() => setPage((p) => p - 1)}
              style={{ color: colors.textSecondary }}
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <span className="text-sm" style={{ color: colors.textSecondary }}>{page} / {pages}</span>
            <Button
              variant="ghost"
              size="sm"
              disabled={page >= pages}
              onClick={() => setPage((p) => p + 1)}
              style={{ color: colors.textSecondary }}
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        )}
      </div>

      {/* Provision Modal */}
      {provisionModal && (
        <ProvisionModal
          user={provisionModal}
          onClose={() => setProvisionModal(null)}
          onSuccess={() => { setProvisionModal(null); fetchUsers(); }}
        />
      )}

      {/* Bulk Provision Modal */}
      {bulkModal && (
        <BulkProvisionModal
          selectedUserIds={selectedUserIds}
          onClose={() => setBulkModal(false)}
          onSuccess={() => { setBulkModal(false); setSelectedUserIds([]); fetchUsers(); }}
        />
      )}

      {/* Delete User Modal */}
      {deleteModal && (
        <DeleteUserModal
          user={deleteModal}
          onClose={() => setDeleteModal(null)}
          onSuccess={() => { setDeleteModal(null); fetchUsers(); }}
        />
      )}

      {/* User Journey Drawer */}
      <UserJourneyDrawer
        user={selectedUser}
        open={!!selectedUser}
        onClose={() => setSelectedUser(null)}
      />
    </AdminLayout>
  );
}

function ProvisionModal({ user, onClose, onSuccess }) {
  const { isDark, colors } = useAdminTheme();
  const [tier, setTier] = useState('premium');
  const [days, setDays] = useState(14);
  const [isTrial, setIsTrial] = useState(true);
  const [loading, setLoading] = useState(false);

  const handleProvision = async () => {
    setLoading(true);
    try {
      await axios.post(`${API}/admin/users/${user.user_id}/provision`, {
        user_id: user.user_id,
        tier,
        duration_days: days,
        is_trial: isTrial,
      }, { withCredentials: true });
      toast.success(`${isTrial ? 'Trial' : 'Subscription'} provisioned for ${user.name || user.email}`);
      onSuccess();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Provisioning failed');
    } finally {
      setLoading(false);
    }
  };

  const inputStyle = { background: colors.input, border: `1px solid ${colors.inputBorder}`, color: colors.text };

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4" onClick={(e) => e.target === e.currentTarget && onClose()} data-testid="provision-modal">
      <div className="w-full max-w-sm rounded-2xl p-6 space-y-4" style={{ background: isDark ? '#1e293b' : '#ffffff', border: `1px solid ${colors.cardBorder}` }}>
        <h3 className="text-base font-semibold" style={{ color: colors.text }}>Provision Subscription</h3>
        <p className="text-sm" style={{ color: colors.textSecondary }}>For: <span style={{ color: colors.text }}>{user.name || user.email}</span></p>

        <div>
          <label className="block text-xs font-medium mb-1.5" style={{ color: colors.textSecondary }}>Plan</label>
          <select value={tier} onChange={(e) => setTier(e.target.value)} className="w-full rounded-xl px-4 py-2.5 text-sm" style={inputStyle} data-testid="provision-tier">
            <option value="premium">Premium ($9.99/mo)</option>
            <option value="share">Share ($19.99/mo)</option>
          </select>
        </div>

        <div>
          <label className="block text-xs font-medium mb-1.5" style={{ color: colors.textSecondary }}>Duration (days)</label>
          <input type="number" value={days} onChange={(e) => setDays(parseInt(e.target.value) || 1)} min={1} max={365} className="w-full rounded-xl px-4 py-2.5 text-sm" style={inputStyle} data-testid="provision-days" />
        </div>

        <label className="flex items-center gap-2 cursor-pointer">
          <input type="checkbox" checked={isTrial} onChange={(e) => setIsTrial(e.target.checked)} className="rounded" style={{ borderColor: colors.inputBorder }} data-testid="provision-trial-checkbox" />
          <span className="text-sm" style={{ color: colors.textSecondary }}>Mark as trial</span>
        </label>

        <div className="flex gap-2 pt-2">
          <Button onClick={onClose} variant="ghost" className="flex-1 rounded-xl" style={{ color: colors.textSecondary }}>Cancel</Button>
          <Button onClick={handleProvision} disabled={loading} className="flex-1 rounded-xl text-white gap-2" style={{ background: colors.accent }} data-testid="provision-submit">
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Gift className="h-4 w-4" />}
            Provision
          </Button>
        </div>
      </div>
    </div>
  );
}


function BulkProvisionModal({ selectedUserIds, onClose, onSuccess }) {
  const { isDark, colors } = useAdminTheme();
  const [tier, setTier] = useState('premium');
  const [days, setDays] = useState(7);
  const [filterTier, setFilterTier] = useState('free');
  const [filterJoinedDays, setFilterJoinedDays] = useState('');
  const [excludePreviousTrials, setExcludePreviousTrials] = useState(false);
  const [preview, setPreview] = useState(null);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [provisioning, setProvisioning] = useState(false);
  const useSelectedOnly = selectedUserIds.length > 0;

  const fetchPreview = async () => {
    setLoadingPreview(true);
    try {
      const payload = {
        tier,
        duration_days: days,
        is_trial: true,
        filter_tier: useSelectedOnly ? 'all' : filterTier,
        filter_joined_days: filterJoinedDays ? parseInt(filterJoinedDays) : null,
        exclude_previous_trials: excludePreviousTrials,
        user_ids: useSelectedOnly ? selectedUserIds : null,
      };
      const res = await axios.post(`${API}/admin/users/bulk-provision/preview`, payload, { withCredentials: true });
      setPreview(res.data);
    } catch (err) {
      toast.error('Failed to preview');
    } finally {
      setLoadingPreview(false);
    }
  };

  useEffect(() => { fetchPreview(); }, [tier, filterTier, filterJoinedDays, excludePreviousTrials, selectedUserIds]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleProvision = async () => {
    if (!preview || preview.will_provision === 0) { toast.error('No users to provision'); return; }
    setProvisioning(true);
    try {
      const payload = {
        tier,
        duration_days: days,
        is_trial: true,
        filter_tier: useSelectedOnly ? 'all' : filterTier,
        filter_joined_days: filterJoinedDays ? parseInt(filterJoinedDays) : null,
        exclude_previous_trials: excludePreviousTrials,
        user_ids: useSelectedOnly ? selectedUserIds : null,
      };
      const res = await axios.post(`${API}/admin/users/bulk-provision`, payload, { withCredentials: true });
      toast.success(res.data.message);
      onSuccess();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Bulk provisioning failed');
    } finally {
      setProvisioning(false);
    }
  };

  const inputStyle = { background: colors.input, border: `1px solid ${colors.inputBorder}`, color: colors.text };

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4" onClick={e => e.target === e.currentTarget && onClose()} data-testid="bulk-provision-modal">
      <div className="w-full max-w-md rounded-2xl p-6 space-y-4" style={{ background: isDark ? '#1e293b' : '#ffffff', border: `1px solid ${colors.cardBorder}` }}>
        <div className="flex items-center justify-between">
          <h3 className="text-base font-semibold flex items-center gap-2" style={{ color: colors.text }}>
            <UsersIcon className="h-4 w-4 text-emerald-400" /> Bulk Provision Trials
          </h3>
          <button onClick={onClose} className="p-1 rounded-full" style={{ color: colors.textSecondary }}><X className="h-4 w-4" /></button>
        </div>

        {useSelectedOnly && (
          <div className="rounded-xl bg-indigo-500/10 border border-indigo-500/20 p-3 text-xs text-indigo-300">
            Provisioning {selectedUserIds.length} selected user(s) only.
          </div>
        )}

        {/* Filters (only when not using selected users) */}
        {!useSelectedOnly && (
          <div className="space-y-3">
            <div>
              <label className="block text-xs font-medium mb-1.5" style={{ color: colors.textSecondary }}>Filter by Current Tier</label>
              <select value={filterTier} onChange={e => setFilterTier(e.target.value)} className="w-full rounded-xl px-4 py-2.5 text-sm" style={inputStyle} data-testid="bulk-filter-tier">
                <option value="all">All Tiers</option>
                <option value="free">Free Users Only</option>
                <option value="paid">Paid Users Only</option>
                <option value="premium">Premium Users Only</option>
                <option value="share">Share Users Only</option>
              </select>
            </div>

            <div>
              <label className="block text-xs font-medium mb-1.5" style={{ color: colors.textSecondary }}>Joined Within (days)</label>
              <input
                type="number" value={filterJoinedDays} onChange={e => setFilterJoinedDays(e.target.value)}
                placeholder="e.g. 30 (leave empty for all)"
                className="w-full rounded-xl px-4 py-2.5 text-sm placeholder-slate-500"
                style={inputStyle}
                data-testid="bulk-filter-days"
              />
            </div>

            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={excludePreviousTrials} onChange={e => setExcludePreviousTrials(e.target.checked)} className="rounded" style={{ borderColor: colors.inputBorder }} data-testid="bulk-exclude-trials" />
              <span className="text-sm" style={{ color: colors.textSecondary }}>Exclude previous trial recipients</span>
            </label>
          </div>
        )}

        {/* Trial Settings */}
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-xs font-medium mb-1.5" style={{ color: colors.textSecondary }}>Trial Plan</label>
            <select value={tier} onChange={e => setTier(e.target.value)} className="w-full rounded-xl px-4 py-2.5 text-sm" style={inputStyle} data-testid="bulk-tier">
              <option value="premium">Premium ($9.99)</option>
              <option value="share">Share ($19.99)</option>
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium mb-1.5" style={{ color: colors.textSecondary }}>Duration (days)</label>
            <input type="number" value={days} onChange={e => setDays(parseInt(e.target.value) || 1)} min={1} max={90} className="w-full rounded-xl px-4 py-2.5 text-sm" style={inputStyle} data-testid="bulk-days" />
          </div>
        </div>

        {/* Preview */}
        <div className="rounded-xl p-4" style={{ background: colors.input, border: `1px solid ${colors.inputBorder}` }} data-testid="bulk-preview">
          {loadingPreview ? (
            <div className="flex items-center justify-center py-2"><Loader2 className="h-4 w-4 animate-spin" style={{ color: colors.textMuted }} /></div>
          ) : preview ? (
            <div className="space-y-1.5 text-sm">
              <div className="flex justify-between"><span style={{ color: colors.textSecondary }}>Matching users</span><span className="font-medium" style={{ color: colors.text }}>{preview.total_matching}</span></div>
              {preview.excluded_previous_trials > 0 && (
                <div className="flex justify-between"><span style={{ color: colors.textSecondary }}>Excluded (prev. trials)</span><span className="text-amber-400">-{preview.excluded_previous_trials}</span></div>
              )}
              <div className="flex justify-between pt-1.5" style={{ borderTop: `1px solid ${colors.cardBorder}` }}><span className="font-medium" style={{ color: colors.textSecondary }}>Will provision</span><span className="text-emerald-400 font-bold">{preview.will_provision}</span></div>
            </div>
          ) : null}
        </div>

        <div className="flex gap-2 pt-1">
          <Button onClick={onClose} variant="ghost" className="flex-1 rounded-xl" style={{ color: colors.textSecondary }}>Cancel</Button>
          <Button
            onClick={handleProvision}
            disabled={provisioning || !preview || preview.will_provision === 0}
            className="flex-1 rounded-xl bg-emerald-600 hover:bg-emerald-500 text-white gap-2"
            data-testid="bulk-provision-submit"
          >
            {provisioning ? <Loader2 className="h-4 w-4 animate-spin" /> : <Gift className="h-4 w-4" />}
            Provision {preview?.will_provision ?? 0} Users
          </Button>
        </div>
      </div>
    </div>
  );
}


function DeleteUserModal({ user, onClose, onSuccess }) {
  const { isDark, colors } = useAdminTheme();
  const [adminPassword, setAdminPassword] = useState('');
  const [mfaCode, setMfaCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleDelete = async () => {
    if (!adminPassword || mfaCode.length !== 6) {
      setError('Password and 6-digit 2FA code are required');
      return;
    }
    setError('');
    setLoading(true);
    try {
      await axios.delete(`${API}/admin/users/${user.user_id}/delete`, {
        data: { admin_password: adminPassword, totp_code: mfaCode },
        withCredentials: true,
      });
      toast.success(`User ${user.email} permanently deleted`);
      onSuccess();
    } catch (err) {
      setError(err?.response?.data?.detail || 'Deletion failed');
    } finally {
      setLoading(false);
    }
  };

  const inputStyle = { background: colors.input, border: `1px solid ${colors.inputBorder}`, color: colors.text };

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4" onClick={e => e.target === e.currentTarget && onClose()} data-testid="delete-user-modal">
      <div className="w-full max-w-sm rounded-2xl p-6 space-y-4" style={{ background: isDark ? '#1e293b' : '#ffffff', border: '1px solid rgba(239,68,68,0.3)' }}>
        <div className="flex items-center gap-2">
          <Trash2 className="h-5 w-5 text-red-500" />
          <h3 className="text-base font-semibold text-red-400">Delete User Permanently</h3>
        </div>

        <div className="rounded-xl p-3 text-xs space-y-1" style={{ background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.2)' }}>
          <p style={{ color: colors.text }}><span className="font-semibold">{user.name || 'Unknown'}</span></p>
          <p style={{ color: colors.textMuted }}>{user.email}</p>
          <p className="text-red-400 font-medium mt-2">This will permanently delete all user data including messages, conversations, and settings. This cannot be undone.</p>
        </div>

        <div>
          <label className="block text-xs font-medium mb-1.5" style={{ color: colors.textSecondary }}>Your Admin Password</label>
          <input
            type="password" value={adminPassword} onChange={e => setAdminPassword(e.target.value)}
            className="w-full rounded-xl px-4 py-2.5 text-sm" style={inputStyle}
            placeholder="Enter your admin password" data-testid="delete-admin-password"
          />
        </div>

        <div>
          <label className="block text-xs font-medium mb-1.5" style={{ color: colors.textSecondary }}>
            <div className="flex items-center gap-1.5"><Shield className="h-3.5 w-3.5 text-indigo-400" /> 2FA Code</div>
          </label>
          <input
            type="text" inputMode="numeric" maxLength={6} value={mfaCode}
            onChange={e => setMfaCode(e.target.value.replace(/\D/g, ''))}
            className="w-full rounded-xl px-4 py-2.5 text-sm font-mono tracking-widest" style={inputStyle}
            placeholder="6-digit code" data-testid="delete-mfa-code"
          />
        </div>

        {error && <p className="text-xs text-red-400">{error}</p>}

        <div className="flex gap-2 pt-2">
          <Button onClick={onClose} variant="ghost" className="flex-1 rounded-xl" style={{ color: colors.textSecondary }}>Cancel</Button>
          <Button
            onClick={handleDelete} disabled={loading || !adminPassword || mfaCode.length !== 6}
            className="flex-1 rounded-xl bg-red-600 hover:bg-red-500 text-white gap-2"
            data-testid="delete-confirm-btn"
          >
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
            Delete Permanently
          </Button>
        </div>
      </div>
    </div>
  );
}
